import React, { useEffect, useState } from 'react';
import {
  Divider,
  Stack,
} from '@mui/material';
import { useParams } from 'react-router-dom';

import { useGetRelatedFrameworkAgreements } from '../api/getRelatedFrameworkAgreements';

import { RelatedFrameworkAgreementsListItem } from './RelatedFrameworkAgreementsListItems';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';
import { Search } from 'components/Search';
import { useDebouncedCallback } from 'use-debounce';
import { NotFound } from 'components/NotFound/NotFound';
import { useAuth } from 'lib/auth';
import { Loader } from 'components/Loader';
import { CheckboxFilter } from 'components/Filter/Inputs/CheckboxFilter';
import { PremiumBanner } from '../../subscriptions/components/PremiumBanner';

export const FrameworkAgreementsTab = ({ id, currentTab, activeCount, expiredCount, companyType }) => {
  const frameworkAgreementsTabName = "agreements";
  const { account, user } = useAuth();
  // const { id } = useParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    includeInactive: false,
    includeExpired: false,
    sortBy: 'dateCreated',
  });
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(null);

  const {
    data: { frameworkAgreements, total },
    refetch: refetchFrameworkAgreements,
    isFetching,
  } = useGetRelatedFrameworkAgreements(id, {
    search,
    ...filters,
    page: page - 1,
    limit: pageSize,
    queryOptions: {
      onSettled: () => {
        clearTimeout(timer);
        setTimeout(() => setLoading(false), 1000);
      },
      enabled: currentTab === frameworkAgreementsTabName
    },
  });

  const handlePageChange = (_, page) => setPage(page);
  const handleFilterUpdate = ({ target: { name, checked } }) => {
    setFilters((filters) => {
      return { ...filters, [name]: checked };
    });
  };
  const debouncedSearchChange = useDebouncedCallback((search) => setSearch(search), 300);
  const handleSearchChange = ({ target: { value: search } }) => {
    debouncedSearchChange(search);
  };

  useTabReset();
  useRefetchFrameworkAgreements();

  const additionalSilver = activeCount - total;
  const additionalGold = additionalSilver + expiredCount;
  // let messageSilver = `There are ${additionalSilver} more Agreements available for Silver`;
  // if (additionalSilver === 1) {
  //   messageSilver = `There is ${additionalSilver} more Agreement available for Silver`
  // }

  let messageGold = `There are ${additionalGold} more Agreements available for Premium members`;
  if (additionalGold === 1) {
    messageGold = `There is ${additionalGold} more Agreement available for Premium members`
  }

  useEffect(() => {
    if (isFetching) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isFetching]);

  if (currentTab !== frameworkAgreementsTabName) return null;

  return (
    <>
      <Stack gap={2} height={1}>
        {!account.super_admin && (
          <>
          {(!user.Company.is_premium || user.Company.subscription_level === 'free') && (
            <PremiumBanner sx={{mt:-2, mb:0, ml: -4, width: { xs: 768, lg: 900, xl: 1200 }}} message={`${messageGold}. Click here for more information`} handleSuccess={refetchFrameworkAgreements} currentLevel={user.Company.subscription_level} />
          )}

          {/* {(user.Company.is_premium && user.Company.subscription_level !== 'gold') && (
            <PremiumBanner sx={{mt:-2, mb:0, ml: -4, width: { xs: 768, lg: 900, xl: 1200 }}} message={`${messageGold}. Click here for more information`} handleSuccess={refetchFrameworkAgreements} currentLevel={user.Company.subscription_level} />
          )} */}
          </>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} width={1}>
          <Search handleSearchChange={handleSearchChange} sx={{minWidth:200, width: 270}} />
          <Stack direction="row" gap={2} sx={{ flex: '1' }}>
            <CheckboxFilter
              adminOnly
              name="includeInactive"
              onChange={handleFilterUpdate}
              checked={filters.includeInactive}
              label="Inactive"
            />
            <CheckboxFilter
              name="includeExpired"
              onChange={handleFilterUpdate}
              checked={filters.includeExpired}
              label={`Expired (${expiredCount})`}
              subscriptionTypes={['silver', 'gold']}
              tooltip="Available only for Premium members"
            />
          </Stack>
        </Stack>
        <Divider />

        {isFetching && !loading ? 
        (
          <></>
        ) :
        (!isFetching || (isFetching && loading)) ?
        (
          <>
          {loading && (
            <Loader />
          )}

          {!loading && (
            <>
            <Stack gap={1} flex={1}>
              {(!frameworkAgreements?.length || frameworkAgreements?.length === 0) && (
                <NotFound
                    title={`No related Framework Agreements found`}
                    caption={
                    search
                        ? 'Please try another search term'
                        : ''
                    }
                    wrapperSx={{ mt: 19 }}
                />
              )}

              {frameworkAgreements.map(( fa) => (
                <RelatedFrameworkAgreementsListItem
                  key={fa.fa_id}
                  companyID={id}
                  companyType={companyType}
                  {...fa}
                />
              ))}
            </Stack>

            <Stack direction="row" justifyContent="center">
              <Pagination
                page={page}
                handlePageChange={handlePageChange}
                itemCount={total}
                itemsPerPage={pageSize}
                onItemsPerPageChange={setPageSize}
              />
            </Stack>
            </>
          )}
        </>
        ) :
        (
          <></>
        )}
      </Stack>
    </>
  );

   function useRefetchFrameworkAgreements() {
    useEffect(() => {
      refetchFrameworkAgreements();
    }, [page, pageSize, filters, search]);
  }

  function useTabReset() {
    useEffect(() => {
      if (currentTab !== frameworkAgreementsTabName) {
        setPage(1);
        setSearch('');
        handleFilterUpdate({target: {name: "includeInactive", checked: false}});
        handleFilterUpdate({target: {name: "includeExpired", checked: false}});
      }
    }, [currentTab]);
  }
};
